/* eslint-disable no-underscore-dangle */
import DashboardLayout from "pages/LayoutContainers/DashboardLayout";
import DashboardNavbar from "pages/Navbars/DashboardNavbar";
import React, { useCallback, useRef, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { protectedResources } from "authConfig";
import { useCurrentUser } from "context/UserContext";
import { useMsal } from "@azure/msal-react";
import { useNativeAuth } from "context/NativeAuthContext";

const API_BASE = protectedResources.apiTodoList.endpoint;
const SCOPES = protectedResources.apiTodoList.scopes?.[0]
  ? [protectedResources.apiTodoList.scopes[0]]
  : [];
const ACCEPT = ".pdf";
const MAX_MB = 10;

// A bearer token for the multipart upload (useFetchWithMsal JSON-encodes, so we post FormData directly).
function useAuthToken() {
  const { instance } = useMsal();
  const { getAccessToken } = useNativeAuth();
  return useCallback(async () => {
    const native = getAccessToken();
    if (native) return native;
    const account = instance.getActiveAccount();
    if (account && SCOPES.length) {
      const s = await instance.acquireTokenSilent({ scopes: SCOPES, account });
      return s?.accessToken || null;
    }
    return null;
  }, [instance, getAccessToken]);
}

function UploadExternalData() {
  const getToken = useAuthToken();
  const { role } = useCurrentUser();
  const isAdmin = ["RISK_ADMIN", "SUPER_RISK_ADMIN", "CATEGORY_OWNER"].includes(
    (role || "").toUpperCase()
  );

  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [themeTag, setThemeTag] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState({ text: "", severity: "info" });

  const pickFile = (f) => {
    if (!f) return;
    if (f.size > MAX_MB * 1024 * 1024) {
      setMsg({ text: `File is too large (max ${MAX_MB} MB).`, severity: "error" });
      return;
    }
    setFile(f);
    setMsg({ text: "", severity: "info" });
  };

  const handleUpload = async () => {
    if (!file || busy) return;
    setBusy(true);
    setMsg({ text: "", severity: "info" });
    try {
      const token = await getToken();
      const fd = new FormData();
      fd.append("file", file);
      if (themeTag.trim()) fd.append("themeTag", themeTag.trim());
      const res = await fetch(`${API_BASE}/anticipateHorizonScan/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const json = await res.json().catch(() => ({}));
      if (res.ok && Array.isArray(json?.data?.signals)) {
        const d = json.data;
        const themes = [...new Set(d.signals.map((s) => s.theme).filter(Boolean))];
        setMsg({
          text: `${json.message || `Extracted ${d.count} signal(s).`}${
            themes.length ? ` Themes: ${themes.join(", ")}.` : ""
          }${d.attached ? "" : " (file text ingested; original not stored)"}`,
          severity: "success",
        });
        setFile(null);
        setThemeTag("");
        if (fileInputRef.current) fileInputRef.current.value = "";
      } else {
        setMsg({ text: json?.message || "Upload failed.", severity: "error" });
      }
    } catch (e) {
      setMsg({ text: "Upload failed — please try again.", severity: "error" });
    }
    setBusy(false);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box sx={{ p: { xs: 1, md: 2 }, maxWidth: 720 }}>
        <MDTypography variant="h5" fontWeight="bold" sx={{ color: "#52009b", mb: 0.5 }}>
          Upload External Data
        </MDTypography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
          Upload an internal PDF report. We extract its distinct signals, classify each into the
          right risk theme, and fold them into Horizon Scan as an internal source — one document can
          populate several themes.
        </Typography>

        {!isAdmin ? (
          <Alert severity="info">You need admin access to upload external data.</Alert>
        ) : (
          <Card variant="outlined">
            <CardContent>
              {/* Drop / pick zone */}
              <Box
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  pickFile(e.dataTransfer.files?.[0]);
                }}
                sx={{
                  border: "1.5px dashed #c9a8e0",
                  borderRadius: 2,
                  p: 3,
                  textAlign: "center",
                  cursor: "pointer",
                  bgcolor: "#faf9fc",
                  "&:hover": { bgcolor: "#f3ecfa" },
                }}
              >
                <UploadFileIcon sx={{ fontSize: 32, color: "#52009b" }} />
                <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 600 }}>
                  {file ? file.name : "Click or drop a file to upload"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  PDF · up to {MAX_MB} MB
                </Typography>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ACCEPT}
                  hidden
                  onChange={(e) => pickFile(e.target.files?.[0])}
                />
              </Box>

              <TextField
                fullWidth
                size="small"
                label="Fallback theme (optional)"
                placeholder="e.g. Aircraft parts supply chain"
                value={themeTag}
                onChange={(e) => setThemeTag(e.target.value)}
                sx={{ mt: 2 }}
                helperText="Signals are auto-classified into risk themes. This is only used for any signal that can't be classified."
              />

              {msg.text ? (
                <Alert severity={msg.severity} sx={{ mt: 2 }}>
                  {msg.text}
                </Alert>
              ) : null}

              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                <MDButton
                  variant="gradient"
                  color="dark"
                  disabled={!file || busy}
                  onClick={handleUpload}
                  sx={{ bgcolor: "#52009b" }}
                  startIcon={
                    busy ? <CircularProgress size={16} color="inherit" /> : <UploadFileIcon />
                  }
                >
                  {busy ? "Uploading…" : "Upload to Horizon Scan"}
                </MDButton>
              </Box>
            </CardContent>
          </Card>
        )}
      </Box>
    </DashboardLayout>
  );
}

export default UploadExternalData;
